/**
 * Which payment tab should be showing.
 *
 * Two independent things can withhold a tab, and conflating them is what made the
 * accepted-offer deep link unreliable: `getPaymentTabVisibility` reflects a payment
 * *session* already in flight, while the operator's config decides whether a tab is on
 * offer for this debt at all. A `?tab=plan` link naming a tab the operator does not offer
 * has to fall back — `Payment.tsx` renders neither the trigger nor the body in that case,
 * so leaving the tab selected shows an empty payment area rather than anything actionable.
 */

import type { PaymentConfiguration } from '@/types';
import type { DiscountOptionKey } from './discount-offers';
import { isPaymentPlanEligible } from './paymentPlans';

/** Every payment tab, in the order a fallback walks: cheapest action for the customer first. */
export const PAYMENT_TABS = ['full', 'partial', 'plan'] as const;
export type PaymentTab = (typeof PAYMENT_TABS)[number];
export const isPaymentTab = (value: unknown): value is PaymentTab => (PAYMENT_TABS as readonly unknown[]).includes(value);

/** The discount each tab prices a debt with. A part payment never carries one. */
export const TAB_DISCOUNT = { full: 'pay_now', partial: null, plan: 'payment_plan' } as const satisfies Record<PaymentTab, DiscountOptionKey | null>;

export interface PaymentTabAvailability {
  full: boolean;
  partial: boolean;
  plan: boolean;
}

/** The tabs the operator offers on a balance, before any payment session in flight withholds one. */
export const paymentTabsOnOffer = (balance: number, payment: PaymentConfiguration): PaymentTabAvailability => ({
  full: true,
  partial: payment.allowPartialPayment && payment.partialPaymentTab && balance >= payment.minDebtForPartialPayment,
  plan: payment.enablePaymentPlan && payment.planPaymentMethods.length > 0 && isPaymentPlanEligible(balance, payment.paymentPlan),
});

/**
 * The tab that should be active: `current` while it is still available, else the first that
 * is.
 *
 * Returns `current` unchanged when nothing is available, so a caller driving state off this
 * cannot be pushed between two impossible tabs on every render. That case is reachable —
 * a full payment session in flight blocks all three.
 */
export function resolveActivePaymentTab(
  current: PaymentTab,
  available: PaymentTabAvailability,
): PaymentTab {
  if (available[current]) return current;
  return PAYMENT_TABS.find((tab) => available[tab]) ?? current;
}
